import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import Card from 'components/card';
import {
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdAdd,
  MdRemove,
  MdLocationOn,
  MdPhone,
  MdCalendarToday,
  MdFilterList,
  MdBusiness,
  MdSearch,
  MdClear,
} from 'react-icons/md';
import headhrService from 'ApiService/Placement/headhrService';
import toast from 'react-hot-toast';
import ConfirmationModal from 'pages/Common/ConfirmationModal';

const AssignStudents = () => {
  const { state } = useContext(AuthContext);
  const [students, setStudents] = useState([]);
  const [hrs, setHrs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedHR, setSelectedHR] = useState('');
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [modalData, setModalData] = useState(null);

  const contextSelectedBranch = state?.selectedBranch;
  const userRole = state?.user?.role;
  const userHrRole = state?.user?.hrRole;
  const userAllPermission = state?.user?.allPermission;

  const isHeadHR = userRole === 'headhr' || userHrRole === 'headhr';
  const isSuperAdmin = userRole === 'superadmin';
  const isJRHRWithPermission =
    (userRole === 'jrhr' || userHrRole === 'jrhr') && userAllPermission;
  const canAssignStudents = isHeadHR || isSuperAdmin || isJRHRWithPermission;
  const canFilterBranch = isHeadHR || isSuperAdmin || isJRHRWithPermission;

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatBatchDates = (startDate, endDate) => {
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    if (!start && !end) return null;
    if (start && end) return `${start} - ${end}`;
    if (start) return `Start: ${start}`;
    return `End: ${end}`;
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchData();
  }, [contextSelectedBranch, filter, selectedBranchFilter, debouncedSearch]);

  const fetchBranches = async () => {
    try {
      const response = await headhrService.getBranches();
      if (response.data.success) {
        setBranches(response.data.branches || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const branchToUse = selectedBranchFilter || contextSelectedBranch;

      const studentsResponse = await headhrService.getAllStudents(
        branchToUse,
        filter,
        debouncedSearch
      );

      if (studentsResponse.data.success) {
        setStudents(studentsResponse.data.data);
      }

      if (canAssignStudents) {
        const hrsResponse = await headhrService.getAllHRs(branchToUse);

        if (hrsResponse.data.success) {
          setHrs(hrsResponse.data.data.filter((hr) => !hr.isHeadHr));
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStudent = (studentId) => {
    if (selectedStudents.includes(studentId)) {
      setSelectedStudents(selectedStudents.filter((id) => id !== studentId));
    } else {
      setSelectedStudents([...selectedStudents, studentId]);
    }
  };

  const confirmAssign = () => {
    if (!selectedHR) {
      toast.error('Please select an HR');
      return;
    }
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    const hrName = hrs.find((h) => h._id === selectedHR)?.name || 'selected HR';
    setModalAction('assign');
    setModalData({
      hrName,
      studentCount: selectedStudents.length,
    });
    setIsModalOpen(true);
  };

  const handleAssign = async () => {
    try {
      const response = await headhrService.assignStudents(
        selectedStudents,
        selectedHR
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setSelectedStudents([]);
        setSelectedHR('');
        fetchData();
      }
    } catch (error) {
      console.error('Error assigning students:', error);
      toast.error('Failed to assign students');
    } finally {
      setIsModalOpen(false);
      setModalAction(null);
      setModalData(null);
    }
  };

  const confirmUnassign = (studentId, hrId, studentName, hrName) => {
    setModalAction('unassign');
    setModalData({
      studentId,
      hrId,
      studentName,
      hrName,
    });
    setIsModalOpen(true);
  };

  const handleUnassign = async () => {
    if (!modalData) return;

    try {
      const response = await headhrService.unassignStudents(
        [modalData.studentId],
        modalData.hrId
      );

      if (response.data.success) {
        toast.success('Student unassigned from HR successfully');
        fetchData();
      }
    } catch (error) {
      console.error('Error unassigning student:', error);
      toast.error('Failed to unassign student');
    } finally {
      setIsModalOpen(false);
      setModalAction(null);
      setModalData(null);
    }
  };

  const renderAssignedHRs = (student) => {
    if (!student.assignedHRs || student.assignedHRs.length === 0) {
      return <span className="text-gray-500">Not Assigned</span>;
    }

    return (
      <div className="flex flex-wrap gap-2">
        {student.assignedHRs.map((assignment) => (
          <div
            key={assignment.hr?._id || assignment.hr}
            className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-sm"
          >
            <span>{assignment.hr?.name || 'Unknown'}</span>
            {canAssignStudents && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  confirmUnassign(
                    student._id,
                    assignment.hr?._id || assignment.hr,
                    student.name,
                    assignment.hr?.name || 'Unknown'
                  );
                }}
                className="ml-1 text-red-500 hover:text-red-700"
                title="Remove this HR"
              >
                <MdRemove className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderPlacementPriority = (priority) => {
    const colors = {
      High: 'bg-red-100 text-red-700',
      Medium: 'bg-yellow-100 text-yellow-700',
      Low: 'bg-gray-100 text-gray-700',
    };
    return (
      <span
        className={`rounded-full px-2 py-1 text-xs font-medium ${
          colors[priority] || colors['Medium']
        }`}
      >
        {priority || 'Medium'}
      </span>
    );
  };

  const renderReadyStatus = (student) => {
    if (student.isPlacementPaused) {
      return (
        <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-700">
          Paused
        </span>
      );
    }
    if (student.readyToPlace) {
      return (
        <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
          Ready
        </span>
      );
    }
    return (
      <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
        Not Ready
      </span>
    );
  };

  const getHRBranchLocations = (hr) => {
    if (hr.hrRole === 'jrhr' && hr.allPermission) {
      return 'All Branches';
    }
    if (!hr.branches || hr.branches.length === 0) return 'No branches assigned';
    return hr.branches.map((b) => b.location || b.name).join(', ');
  };

  const getHRDropdownLabel = (hr) => {
    const roleLabel = hr.hrRole?.toUpperCase();
    const location = getHRBranchLocations(hr);
    return `${hr.name} (${roleLabel}) - ${location}`;
  };

  const selectedHRData = hrs.find((h) => h._id === selectedHR);

  return (
    <div className="mt-3">
      <Card extra="w-full pb-10 p-4">
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
                Assign Students
              </h4>
              <p className="text-sm text-gray-600">
                {canAssignStudents
                  ? 'Students can be assigned to multiple HRs'
                  : 'View students from your assigned branches'}
              </p>
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="rounded-lg border border-gray-300 px-4 py-2"
            >
              <option value="all">All Students</option>
              <option value="unassigned">Unassigned Students</option>
              <option value="assigned">Assigned Students</option>
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-4 rounded-lg bg-gray-50 p-4">
            <div className="flex items-center gap-2">
              <MdFilterList className="h-5 w-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                Filters:
              </span>
            </div>

            <div className="relative flex items-center">
              <MdSearch className="absolute left-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search name, email, phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="min-w-[220px] rounded-lg border border-gray-300 py-2 pl-9 pr-8 text-sm focus:border-brand-500 focus:outline-none"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 text-gray-400 hover:text-gray-600"
                >
                  <MdClear className="h-4 w-4" />
                </button>
              )}
            </div>

            {canFilterBranch && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Branch:</label>
                <select
                  value={selectedBranchFilter}
                  onChange={(e) => setSelectedBranchFilter(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name} - {branch.location}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {canAssignStudents && (
              <>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Assign to HR:</label>
                  <select
                    value={selectedHR}
                    onChange={(e) => setSelectedHR(e.target.value)}
                    className="min-w-[200px] rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">Select HR</option>
                    {hrs.map((hr) => (
                      <option key={hr._id} value={hr._id}>
                        {getHRDropdownLabel(hr)}
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={confirmAssign}
                  disabled={!selectedHR || selectedStudents.length === 0}
                  className="flex items-center gap-1 rounded-lg bg-brand-500 px-4 py-2 text-white disabled:opacity-50"
                >
                  <MdAdd className="h-4 w-4" />
                  Assign ({selectedStudents.length})
                </button>
              </>
            )}

            <button
              onClick={() => {
                setSearchQuery('');
                if (canFilterBranch) setSelectedBranchFilter('');
                setFilter('all');
                setSelectedStudents([]);
                setSelectedHR('');
              }}
              className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50"
            >
              <MdClear className="h-4 w-4" />
              Reset
            </button>
          </div>

          {canAssignStudents && selectedHR && selectedHRData && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-navy-700">
                    {selectedHRData.name}
                  </span>
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
                    {selectedHRData.hrRole?.toUpperCase()}
                  </span>
                  {selectedHRData.hrRole === 'jrhr' &&
                    selectedHRData.allPermission && (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        Full Access
                      </span>
                    )}
                </div>
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <MdPhone className="h-4 w-4" />
                  <span>{selectedHRData.contact || selectedHRData.email}</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <MdBusiness className="h-4 w-4" />
                  <span>
                    Branches:{' '}
                    {selectedHRData.hrRole === 'jrhr' &&
                    selectedHRData.allPermission
                      ? 'All Branches'
                      : selectedHRData.branches?.length > 0
                      ? selectedHRData.branches
                          .map((b) => `${b.name} (${b.location})`)
                          .join(', ')
                      : 'No branches assigned'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr className="border-b bg-gray-50">
                {canAssignStudents && (
                  <th className="pb-3 pl-2 pt-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                    Select
                  </th>
                )}
                <th className="pb-3 pt-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Student
                </th>
                <th className="pb-3 pt-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Branch
                </th>
                <th className="pb-3 pt-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Course / Batch
                </th>
                <th className="pb-3 pt-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Location Pref.
                </th>
                <th className="pb-3 pt-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Salary Pref.
                </th>
                <th className="pb-3 pt-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Status
                </th>
                <th className="pb-3 pt-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Assigned HRs
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={canAssignStudents ? '8' : '7'}
                    className="py-8 text-center text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              ) : students.length === 0 ? (
                <tr>
                  <td
                    colSpan={canAssignStudents ? '8' : '7'}
                    className="py-8 text-center text-gray-500"
                  >
                    No students found
                  </td>
                </tr>
              ) : (
                students.map((student) => (
                  <tr key={student._id} className="border-b hover:bg-gray-50">
                    {canAssignStudents && (
                      <td className="py-3 pl-2">
                        <button
                          onClick={() => handleSelectStudent(student._id)}
                        >
                          {selectedStudents.includes(student._id) ? (
                            <MdCheckBox className="h-5 w-5 text-brand-500" />
                          ) : (
                            <MdCheckBoxOutlineBlank className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </td>
                    )}
                    <td className="py-3">
                      <div>
                        <div className="font-medium text-navy-700">
                          {student.name}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <span>{student.email}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <MdPhone className="h-3 w-3" />
                          <span>{student.contactNumber || '-'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <div>
                        <div className="font-medium text-navy-700">
                          {student.branch?.name || '-'}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <MdLocationOn className="h-3 w-3" />
                          <span>{student.branch?.location || '-'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3">
                      <div>
                        <div className="font-medium text-navy-700">
                          {student.enrollments?.[0]?.course?.courseName || '-'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {student.enrollments?.[0]?.batch?.batchNumber
                            ? `Batch: ${student.enrollments[0].batch.batchNumber}`
                            : '-'}
                        </div>
                        {student.enrollments?.[0]?.batchStructure?.name && (
                          <div className="text-xs text-gray-400">
                            {student.enrollments[0].batchStructure.name}
                          </div>
                        )}
                        {student.enrollments?.[0]?.batch &&
                          (student.enrollments[0].batch.startDate ||
                            student.enrollments[0].batch.endDate) && (
                            <div className="flex items-center gap-1 text-xs text-gray-400">
                              <MdCalendarToday className="h-3 w-3" />
                              <span>
                                {formatBatchDates(
                                  student.enrollments[0].batch.startDate,
                                  student.enrollments[0].batch.endDate
                                )}
                              </span>
                            </div>
                          )}
                      </div>
                    </td>
                    <td className="py-3">
                      {student.jobLocationPreference &&
                      student.jobLocationPreference.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {student.jobLocationPreference
                            .slice(0, 3)
                            .map((loc, idx) => (
                              <span
                                key={idx}
                                className="rounded bg-blue-50 px-2 py-0.5 text-xs text-blue-600"
                              >
                                {loc}
                              </span>
                            ))}
                          {student.jobLocationPreference.length > 3 && (
                            <span className="text-xs text-gray-400">
                              +{student.jobLocationPreference.length - 3} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-semibold ${
                          student.preferredSalary === 'More than 5 LPA'
                            ? 'bg-green-100 text-green-700'
                            : student.preferredSalary === 'Below 5 LPA'
                            ? 'bg-blue-100 text-blue-700'
                            : student.preferredSalary === 'Below 4 LPA'
                            ? 'bg-yellow-100 text-yellow-700'
                            : student.preferredSalary === 'Below 3 LPA'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {student.preferredSalary || 'Any Salary'}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex flex-col gap-1">
                        {renderReadyStatus(student)}
                        {renderPlacementPriority(student.placementPriority)}
                      </div>
                    </td>
                    <td className="py-3">{renderAssignedHRs(student)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setModalAction(null);
          setModalData(null);
        }}
        onConfirm={() => {
          if (modalAction === 'assign') {
            handleAssign();
          } else if (modalAction === 'unassign') {
            handleUnassign();
          }
        }}
        title="Confirm Action"
        message={
          modalAction === 'assign'
            ? `Are you sure you want to assign ${modalData?.studentCount} student(s) to ${modalData?.hrName}?`
            : `Are you sure you want to unassign ${modalData?.studentName} from ${modalData?.hrName}?`
        }
        confirmText="Yes"
        cancelText="Cancel"
        icon="warning"
      />
    </div>
  );
};

export default AssignStudents;
